// Campaign hold state shared by the campaigns list, the campaign report and
// the editor review step. hold_reason and hold_until come from email_campaigns.

export type HoldReason = 'reputation' | 'ramp' | null

export interface HoldLike {
  status?: string
  hold_reason?: HoldReason
  hold_until?: string | null
}

export interface SendPolicy {
  ramp_cutoff: number
  ramp_seed: number
  ramp_hold_hours: number
}

export interface RecentOverlap {
  count: number
  last_sent_at: string | null
  days: number
}

// Label shown in place of "sending" while a hold is active
export function holdStatusLabel(campaign: HoldLike): string | null {
  if (campaign.status !== 'sending' || !campaign.hold_reason) return null
  return campaign.hold_reason === 'ramp' ? 'warming up' : 'held'
}

// Hint text for the report. formatDateTime renders a server datetime with the
// group's date and time format; sent and total are recipient counts.
export function holdHintText(
  campaign: HoldLike,
  sent: number,
  total: number,
  formatDateTime: (value: string) => string
): string | null {
  if (campaign.status !== 'sending' || !campaign.hold_reason) return null
  if (campaign.hold_reason === 'ramp') {
    const when = campaign.hold_until ? `at ${formatDateTime(campaign.hold_until)}` : 'in a few hours'
    return `Warming up: ${sent.toLocaleString()} of ${total.toLocaleString()} sent. Large lists send in stages so deliverability can be checked before the rest goes out. Sending continues ${when}.`
  }
  return 'Sending is held while platform email reputation recovers. This is checked every few minutes and sending resumes on its own; no action is needed.'
}

function hoursLabel(hours: number): string {
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

// Review step line for a large audience. count is the deliverable audience
// (preview count minus hygiene.suppressed_total, the same rows the worker will
// try); policy comes from the preview response's send_policy; null when the
// audience is at or under the cutoff.
export function stagedSendText(count: number | null, policy: SendPolicy | null): string | null {
  if (count === null || !policy || count <= policy.ramp_cutoff) return null
  return `Lists over ${policy.ramp_cutoff.toLocaleString()} send in stages: the first ${policy.ramp_seed.toLocaleString()} go out, sending pauses for ${hoursLabel(policy.ramp_hold_hours)} to check deliverability, then the rest follows. Expect this campaign to finish about ${hoursLabel(policy.ramp_hold_hours)} after it starts.`
}

// Review step warning when half or more of the audience was mailed recently.
export function repeatAudienceText(
  count: number | null,
  overlap: RecentOverlap | null,
  formatDate: (value: string) => string
): string | null {
  if (count === null || count === 0 || !overlap || overlap.count === 0) return null
  if (overlap.count / count < 0.5) return null
  const when = overlap.last_sent_at ? ` The latest went out ${formatDate(overlap.last_sent_at)}.` : ''
  return `${overlap.count.toLocaleString()} of these ${count.toLocaleString()} customers already received a campaign from this location in the last ${overlap.days} days.${when} Sending to the same people again within a week raises spam complaints.`
}
